import React, { useEffect, useState } from 'react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

/**
 * PublicStatusPill — discreet operational trust signal for the public footer.
 *
 * Probes /api/public/status once on mount. No periodic polling (would be
 * unnecessary noise for a marketing footer). On fetch failure or `degraded`,
 * the pill renders amber; on a clean ping it renders emerald. The label is
 * intentionally restrained — no "bank-grade" / "military-grade" hyperbole.
 */
export const PublicStatusPill = ({ className = '' }) => {
  const [status, setStatus] = useState('checking');

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/api/public/status`, { credentials: 'omit' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d) => { if (!cancelled) setStatus(d?.status === 'operational' ? 'operational' : 'degraded'); })
      .catch(() => { if (!cancelled) setStatus('degraded'); });
    return () => { cancelled = true; };
  }, []);

  if (status === 'checking') return null;

  const isUp = status === 'operational';
  const dot = isUp ? 'bg-emerald-400' : 'bg-amber-400';
  const label = isUp ? 'Systems operational' : 'Operations degraded';

  return (
    <span
      className={`inline-flex items-center gap-2 text-[11px] ${className}`}
      style={{ color: 'var(--gpc-ink-soft)' }}
      data-testid="public-status-pill"
      data-status={status}
      title="Operational status of the GP Capital platform"
    >
      <span className={`relative w-1.5 h-1.5 rounded-full ${dot}`}>
        {isUp && <span className="absolute inset-0 rounded-full bg-emerald-400 opacity-60 animate-ping" />}
      </span>
      <span>{label}</span>
    </span>
  );
};

export default PublicStatusPill;
